import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseTreasuryRates {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private currentInterval: string = '1w';

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private changeInterval(interval: string): void {
    this.currentInterval = interval;
    this.setupChart(this.config.symbol!);
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const payload = { symbol, interval: this.currentInterval };
      const res = await apiGet({
        method: "POST",
        url: `${APIURL}/v2/discover/treasury_rates`,
        body: payload,
      });

      if (!res.ok) throw new Error("Failed to fetch treasury rates");

      const data = await res.json();
      this.currentData = data;
      this.updateDisplay();
      this.emit("dataUpdate", this.currentData);
    } catch (err) {
      console.error("Error fetching treasury rates:", err);
    }
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';
    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Treasury Rates 
          </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper> 
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">
          <div class="chartDv" id="eventsContainer" style="height: 100%" data-testing-id="events-container"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
  }

  private updateLastUpdatedTooltip(): void {
    let lastUpdated = 'N/A';
    if (this.currentData && this.currentData.data) {
      const treasuryData = this.currentData.data;
      let latest = null;
      for (const key in treasuryData) {
        if (treasuryData[key]?.last_updated_at) {
          const d = new Date(treasuryData[key].last_updated_at);
          if (!latest || d > latest) latest = d;
        }
      }
      if (latest) {
        const options: Intl.DateTimeFormatOptions = {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York',
        };
        const formatted = latest.toLocaleString('en-US', options).replace(',', '');
        lastUpdated = formatted.replace(/\//g, '-') + ' EST';
      }
    }
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) {
      tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
    }
  }

  private updateDisplay(): void {
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement;

    const hasData =
        this.currentData?.data &&
        Object.keys(this.currentData.data).length > 0;

    // ❌ NO DATA STATE
    if (!hasData) {
        if (container) {
            container.innerHTML = getNoDataHTML();
        }

        chartDv?.removeAttribute('data-testing-id');
        return;
    }

    // ✅ VALID DATA STATE → render UI first
    this.renderAnalystRating();

    // 🔥 confirm UI is actually rendered
    const isRendered =
        container &&
        container.innerHTML.trim().length > 0 &&
        !container.innerHTML.includes('No data available');

    if (isRendered && chartDv) {
        chartDv.setAttribute('data-testing-id', 'TreasuryRates');
    } else {
        chartDv?.removeAttribute('data-testing-id');
    }

    this.updateLastUpdatedTooltip();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    if (!container) return;

    const treasuryData = this.currentData?.data;
    if (!treasuryData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    let html = `
    <div class="treasuryRatesMain" data-testing-id="treasury-rates-table">
      <div class="item header" data-testing-id="table-header">
        <p data-testing-id="header-bond">BOND</p>
        <p data-testing-id="header-change">CHANGE%</p>
        <p data-testing-id="header-rate">RATE</p>
      </div>
    `;

    let index = 0;
    for (const [duration, info] of Object.entries(treasuryData)) {
      const changeClass = (info as any).change > 0 ? 'up' : (info as any).change < 0 ? 'down' : 'neutral';
      const safeId = duration.toLowerCase().replace(/[^a-z0-9]/g, '-');
      
      html += `
        <div class="item body" 
             data-testing-id="treasury-row-${safeId}" 
             data-index="${index}" 
             data-bond="${duration}" 
             data-change="${(info as any).change}"
             data-rate="${(info as any).rate}">
          <p data-testing-id="cell-bond">${duration}</p>
          <p class="rangeFigure ${(info as any).change >= 0 ? 'green' : 'red'}" data-testing-id="cell-change">
            ${(info as any).change.toFixed(2)}%
          </p>
          <p class="rateinfo" data-testing-id="cell-rate">${(info as any).rate.toFixed(2)}</p>
        </div>
      `;
      index++;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseTreasuryRates: typeof TraderverseTreasuryRates; } }
if (typeof window !== 'undefined') { window.TraderverseTreasuryRates = TraderverseTreasuryRates; }